/**
 * Neural Index Calculation Algorithm
 *
 * The Neural Index (NI) is a 0-100 score representing "Brain Permission"
 * 100% is the baseline in optimal air conditions
 *
 * Based on scientific research correlating air quality with cognitive performance:
 *
 * INPUTS (Environmental Dataset):
 * - Molecular Density: PM2.5 & PM10 (fine dust crossing blood-brain barrier)
 * - Gaseous Interference: CO2, NO2, O3 (drivers of neural drowsiness)
 * - Atmospheric Latency: Humidity & Temperature (affect toxin residence time)
 *
 * OUTPUTS (Performance Metrics):
 * - Neural Index (0-100): Overall brain permission score
 * - Cognitive Debt: Productivity loss percentage
 * - Reaction Latency: Estimated ms increase in reaction time
 * - Error Rate Increase: Estimated % increase in task errors
 * - Task Duration Impact: Multiplier for time to complete cognitive tasks
 */

import type { AirQualityData, NeuralMetrics, MolecularPermissionLevel, AtmosphericLatencyLevel } from '@/types';

/** Weather conditions that affect pollutant dispersion */
interface AtmosphericConditions {
  temperature: number; // Celsius
  humidity: number; // Percentage (0-100)
  windSpeed: number; // km/h
}

/** Extended cognitive performance metrics */
export interface CognitivePerformanceMetrics {
  neuralIndex: number;          // 0-100 score
  cognitiveDebt: number;        // % productivity loss
  reactionLatencyMs: number;    // Added ms to reaction time
  errorRateIncrease: number;    // % increase in errors
  taskDurationMultiplier: number; // 1.0 = normal, 1.5 = 50% slower
  focusCapacity: number;        // 0-100% ability to concentrate
  recoveryMinutes: number;      // Minutes of clean air to recover
}

/**
 * Calculate the Neural Index from air quality data
 * Uses multi-factor weighted model based on cognitive research
 *
 * @param data Air quality readings
 * @param conditions Optional atmospheric conditions
 * @returns Neural Index score (0-100)
 */
export function calculateNeuralIndex(
  data: AirQualityData,
  conditions?: AtmosphericConditions
): number {
  // Core pollutant scores
  const pm25Score = calculatePM25Score(data.pm25.value);
  const ozoneScore = calculateOzoneScore(data.ozone.value);
  const co2Score = calculateCO2Score(data.co2.value);

  // Additional gaseous interference
  const no2Score = data.no2 ? calculateNO2Score(data.no2.value) : 100;
  const pm10Score = data.pm10 ? calculatePM10Score(data.pm10.value) : 100;

  // Base weighted combination
  // PM2.5 has highest impact - crosses blood-brain barrier
  let weightedScore =
    pm25Score * 0.35 +   // 35% - PM2.5 most impactful (blood-brain barrier)
    ozoneScore * 0.20 +  // 20% - Ozone causes oxidative stress
    co2Score * 0.20 +    // 20% - CO2 directly impairs cognition
    no2Score * 0.15 +    // 15% - NO2 causes neural inflammation
    pm10Score * 0.10;    // 10% - PM10 respiratory burden

  // Apply atmospheric latency factor if conditions available
  if (conditions) {
    const latencyFactor = calculateAtmosphericLatencyPenalty(conditions);
    // Higher latency = pollutants stay near breathing zone longer
    weightedScore *= (1 - latencyFactor * 0.15); // Up to 15% additional penalty
  }

  // Clamp to 0-100 range
  return Math.max(0, Math.min(100, Math.round(weightedScore)));
}

/**
 * PM2.5 contribution to Neural Index
 * PM2.5 crosses the blood-brain barrier, causing neuroinflammation
 * Based on EPA guidelines + cognitive research (Zhang et al., 2018)
 *
 * Studies show 10µg/m³ increase = 1.5% cognitive decline
 */
function calculatePM25Score(pm25: number): number {
  if (pm25 <= 12) return 100;                           // Good: Full capacity
  if (pm25 <= 35.4) return 100 - ((pm25 - 12) / 23.4) * 15;    // Moderate: -15% max
  if (pm25 <= 55.4) return 85 - ((pm25 - 35.4) / 20) * 20;     // USG: -20% more
  if (pm25 <= 150.4) return 65 - ((pm25 - 55.4) / 95) * 25;    // Unhealthy: -25% more
  if (pm25 <= 250.4) return 40 - ((pm25 - 150.4) / 100) * 20;  // Very Unhealthy
  return Math.max(0, 20 - ((pm25 - 250.4) / 250) * 20);        // Hazardous
}

/**
 * PM10 contribution to Neural Index
 * Larger particles affect respiratory function, indirectly impacting cognition
 */
function calculatePM10Score(pm10: number): number {
  if (pm10 <= 54) return 100;
  if (pm10 <= 154) return 100 - ((pm10 - 54) / 100) * 10;
  if (pm10 <= 254) return 90 - ((pm10 - 154) / 100) * 15;
  if (pm10 <= 354) return 75 - ((pm10 - 254) / 100) * 20;
  return Math.max(0, 55 - ((pm10 - 354) / 150) * 35);
}

/**
 * NO2 contribution to Neural Index
 * NO2 causes neural inflammation and reduces oxygen delivery to brain
 * Traffic pollution primary source
 */
function calculateNO2Score(no2: number): number {
  if (no2 <= 53) return 100;                            // Good
  if (no2 <= 100) return 100 - ((no2 - 53) / 47) * 10;         // Moderate
  if (no2 <= 360) return 90 - ((no2 - 100) / 260) * 25;        // Unhealthy
  if (no2 <= 649) return 65 - ((no2 - 360) / 289) * 30;        // Very Unhealthy
  return Math.max(0, 35 - ((no2 - 649) / 351) * 35);           // Hazardous
}

/**
 * Calculate atmospheric latency penalty
 * High humidity + low wind + temperature inversion = pollutants stay in breathing zone longer
 */
function calculateAtmosphericLatencyPenalty(conditions: AtmosphericConditions): number {
  let penalty = 0;

  // High humidity traps pollutants (>70% humidity increases exposure)
  if (conditions.humidity > 70) {
    penalty += (conditions.humidity - 70) / 100; // Up to 0.3 penalty
  }

  // Low wind speed prevents dispersion (<5 km/h is stagnant)
  if (conditions.windSpeed < 5) {
    penalty += (5 - conditions.windSpeed) / 10; // Up to 0.5 penalty
  }

  // Temperature inversion (cold air traps pollution)
  // Also extreme heat increases ozone formation
  if (conditions.temperature < 5) {
    penalty += 0.2; // Cold inversion layer
  } else if (conditions.temperature > 35) {
    penalty += 0.15; // Heat increases ozone
  }

  return Math.min(1, penalty); // Cap at 1.0
}

/**
 * Ozone contribution to Neural Index
 * Ozone affects respiratory and cognitive function
 */
function calculateOzoneScore(ozone: number): number {
  if (ozone <= 54) return 100;                          // Good
  if (ozone <= 70) return 100 - ((ozone - 54) / 16) * 10;      // Moderate
  if (ozone <= 85) return 90 - ((ozone - 70) / 15) * 15;       // USG
  if (ozone <= 105) return 75 - ((ozone - 85) / 20) * 20;      // Unhealthy
  if (ozone <= 200) return 55 - ((ozone - 105) / 95) * 25;     // Very Unhealthy
  return Math.max(0, 30 - ((ozone - 200) / 300) * 30);         // Hazardous
}

/**
 * CO2 contribution to Neural Index
 * Elevated CO2 directly impacts cognitive performance
 */
function calculateCO2Score(co2: number): number {
  if (co2 <= 600) return 100;                           // Optimal
  if (co2 <= 1000) return 100 - ((co2 - 600) / 400) * 10;      // Acceptable: -10% max
  if (co2 <= 1500) return 90 - ((co2 - 1000) / 500) * 15;      // Elevated: -15% more
  if (co2 <= 2000) return 75 - ((co2 - 1500) / 500) * 25;      // High: -25% more
  return Math.max(0, 50 - ((co2 - 2000) / 3000) * 50);         // Dangerous
}

/**
 * Calculate Cognitive Debt (productivity loss percentage)
 * CD = 100 - NI
 */
export function calculateCognitiveDebt(neuralIndex: number): number {
  return Math.max(0, Math.min(100, 100 - neuralIndex));
}

/**
 * Determine Molecular Permission level
 */
export function determineMolecularPermission(
  data: AirQualityData
): MolecularPermissionLevel {
  const pm25 = data.pm25.value;
  const ozone = data.ozone.value;

  // RESTRICTED: High pollution levels
  if (pm25 > 55.4 || ozone > 85) {
    return 'RESTRICTED';
  }

  // CAUTION: Moderate pollution
  if (pm25 > 35.4 || ozone > 70) {
    return 'CAUTION';
  }

  // GRANTED: Good air quality
  return 'GRANTED';
}

/**
 * Determine Atmospheric Latency level
 * Represents how "responsive" the air is to cognitive function
 */
export function determineAtmosphericLatency(
  data: AirQualityData
): AtmosphericLatencyLevel {
  const pm25 = data.pm25.value;
  const aqi = data.aqi;

  if (aqi <= 50 && pm25 <= 12) return 'LOW';
  if (aqi <= 100 && pm25 <= 35) return 'MODERATE';
  if (aqi <= 150 && pm25 <= 55) return 'HIGH';
  return 'CRITICAL';
}

/**
 * Calculate Recovery Latency (minutes of clean air needed)
 * Based on biological half-life of pollutant effects
 */
export function calculateRecoveryLatency(
  currentNI: number,
  targetNI: number = 100
): number {
  const deficit = targetNI - currentNI;
  if (deficit <= 0) return 0;

  // Rough estimate: ~2 minutes of clean air per 1% deficit
  // Plus baseline recovery time
  const baseRecovery = 5; // minimum 5 minutes
  const deficitRecovery = deficit * 2;

  return Math.round(baseRecovery + deficitRecovery);
}

/**
 * Calculate Reaction Latency increase in milliseconds
 * Based on research: Poor air quality slows neural processing
 * Average human reaction time is ~250ms
 */
export function calculateReactionLatency(neuralIndex: number): number {
  const deficit = 100 - neuralIndex;
  // Each 10% deficit adds ~15ms to reaction time
  return Math.round(deficit * 1.5);
}

/**
 * Calculate Error Rate increase percentage
 * Based on research: Cognitive load from pollution increases mistakes
 */
export function calculateErrorRateIncrease(neuralIndex: number): number {
  const deficit = 100 - neuralIndex;
  // Error rate scales exponentially with cognitive deficit
  // At 50% NI, error rate increases by ~25%
  return Math.round(deficit * 0.5 * (1 + deficit / 100));
}

/**
 * Calculate Task Duration multiplier
 * How much longer cognitive tasks take
 */
export function calculateTaskDurationMultiplier(neuralIndex: number): number {
  const deficit = 100 - neuralIndex;
  // At 50% NI, tasks take 1.5x longer
  return 1 + (deficit / 100) * 0.5;
}

/**
 * Calculate Focus Capacity
 * Ability to maintain concentration (affected differently than raw NI)
 */
export function calculateFocusCapacity(neuralIndex: number, co2: number): number {
  // CO2 has outsized impact on focus specifically
  let focusScore = neuralIndex;

  // Additional CO2 penalty for focus
  if (co2 > 1000) {
    focusScore -= (co2 - 1000) / 50; // Each 50ppm above 1000 = 1% focus loss
  }

  return Math.max(0, Math.min(100, Math.round(focusScore)));
}

/**
 * Calculate comprehensive cognitive performance metrics
 */
export function calculateCognitivePerformanceMetrics(
  data: AirQualityData,
  conditions?: AtmosphericConditions
): CognitivePerformanceMetrics {
  const neuralIndex = calculateNeuralIndex(data, conditions);

  return {
    neuralIndex,
    cognitiveDebt: calculateCognitiveDebt(neuralIndex),
    reactionLatencyMs: calculateReactionLatency(neuralIndex),
    errorRateIncrease: calculateErrorRateIncrease(neuralIndex),
    taskDurationMultiplier: calculateTaskDurationMultiplier(neuralIndex),
    focusCapacity: calculateFocusCapacity(neuralIndex, data.co2.value),
    recoveryMinutes: calculateRecoveryLatency(neuralIndex),
  };
}

/**
 * Calculate all Neural Metrics from air quality data
 */
export function calculateNeuralMetrics(data: AirQualityData): NeuralMetrics {
  const neuralIndex = calculateNeuralIndex(data);
  const cognitiveDebt = calculateCognitiveDebt(neuralIndex);
  const molecularPermission = determineMolecularPermission(data);
  const atmosphericLatency = determineAtmosphericLatency(data);
  const recoveryLatency = calculateRecoveryLatency(neuralIndex);

  return {
    neuralIndex,
    cognitiveDebt,
    molecularPermission,
    atmosphericLatency,
    recoveryLatency,
  };
}

/**
 * Predict Neural Index at destination based on route air quality
 */
export function predictArrivalNeuralIndex(
  currentNI: number,
  routeAverageAQI: number,
  durationMinutes: number
): number {
  // Exposure effect: longer time in poor air = more degradation
  const exposureFactor = Math.min(1, durationMinutes / 60);
  const aqiPenalty = (routeAverageAQI / 500) * 30 * exposureFactor;

  return Math.max(0, Math.round(currentNI - aqiPenalty));
}
